import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import { getCombinedNotifications } from '~/modules/notifications/api/get-combined-notifications.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getShoppingCartSummary } from '@modules/ecommerce/api/get-shopping-cart-summary.server';
import { getShoppingCartCount } from '@modules/ecommerce/api/get-shopping-cart-count.server';
import {
    hasFeature,
    isUsProductLaunchEnabled,
    shouldRevalidateAppLayout,
} from '@modules/root';
import { getPermissions } from '~/modules/user';
import { PermissionEnum } from 'generated/graphql';
import { getSession } from '~/modules/root/util/session.server';
import AppPage from '@modules/root/page/AppPage';
import { toggleFeatures } from '~/modules/root/util/toggle-features';
import { getBottomContent } from '@modules/root/api/get-bottom-content.server';
import { getFilterDrawerCookieValue } from '~/modules/search/hook/use-filter-drawer-preference';
import { getMoodBoardPageIds } from '@modules-rocco/mood-board/api/get-mood-board-page-ids.server';
import { getMonthlyUsers } from '~/modules/root/api/get-monthly-users.server';
import { getRecentMagazines } from '~/modules/magazine/api/get-recent-magazines.server';
import { getFooterContent } from '~/modules-rocco/footer/api/get-footer-content.server';
import { adsRequest } from '@modules/adverts/api/ads-request.server';
import { getTopBannerAd } from '@modules/adverts/utils/get-top-banner-ad.server';

/**
 * global catch and error boundary for all pages under __public
 * Child routes can still catch their own but if they don't we'll end up here
 * root.tsx will still render top nav etc
 */
export { ErrorBoundary, links } from '@modules/root/page/AppPage';

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { appDisplayMode, device, user, featureFlags, webSocketUrl } =
        context;
    const response = toggleFeatures(request);
    if (response) throw response;

    const footerRedesignEnabled =
        hasFeature(featureFlags, 'footer_redesign_dec_2025') &&
        appDisplayMode === 'desktop';

    // Site-wide top banner ad: only requested when the feature flag is on. The
    // slot renders solely when a creative comes back for the current page/market.
    const topBannerEnabled = hasFeature(featureFlags, 'top_ad_banner');
    const topBannerAdPromise: Promise<ReturnType<typeof getTopBannerAd>> =
        topBannerEnabled
            ? (async () =>
                  getTopBannerAd(
                      await adsRequest({
                          type: 'SiteWideBannerGroup',
                          requestUrl: request.url,
                          ...context,
                      }),
                  ))().catch(() => null)
            : Promise.resolve(null);

    const url = new URL(request.url);

    // The shopping cart only exists in markets with ecommerce/products. Skipping
    // the fetch for non-product markets (e.g. US) avoids a needless GraphQL
    // request and the server-side "Invalid tax provider" error it would
    // otherwise trigger while resolving cart tax totals.
    const shoppingCartAvailable = isUsProductLaunchEnabled(
        context.localisation.countryConfig,
        featureFlags,
    );

    if (
        url.pathname.startsWith('/search') &&
        appDisplayMode !== 'desktop' &&
        !device.isBot
    ) {
        const [
            notifications,
            cartCount,
            abTestConfig,
            session,
            moodBoardPageIds,
            monthlyUsers,
            topBannerAd,
        ] = await Promise.all([
            getCombinedNotifications(context),
            shoppingCartAvailable
                ? getShoppingCartCount(context)
                : Promise.resolve(0),
            context.getABTests(),
            getSession(request),
            getMoodBoardPageIds(context),
            getMonthlyUsers(context),
            topBannerAdPromise,
        ]);

        // This lightweight path skips the fuller cart summary, so source the header
        // badge count from the dedicated count query instead.
        notifications.cart.count = cartCount;

        const bookConsultationEnabled = false;

        const ecommerceActiveEnabled = hasFeature(
            featureFlags,
            'ecommerce_active',
        );

        const availableForWorkEnabled = hasFeature(
            featureFlags,
            'available_for_work',
        );
        return routerData(
            {
                user,
                featureFlags,
                notifications,
                localisation: null,
                shoppingCartSummary: null,
                webSocketUrl,
                ecommerceActiveEnabled,
                bookConsultationEnabled,
                canApproveContent: getPermissions(
                    user,
                    PermissionEnum.BmContentApprove,
                ),
                canCreateProfessional: getPermissions(
                    user,
                    PermissionEnum.BmProfessionalCreate,
                ),
                toastMessage: session.getToastMessage(),
                abTestConfig,
                showReseneSponsor: false,
                availableForWorkEnabled,
                professionalBookingLink: null,
                moodBoardPageIds,
                bottomContent: null,
                filterDrawerCookieValue: getFilterDrawerCookieValue(request),
                monthlyUsers,
                footerRedesignEnabled: false,
                recentMagazines: [],
                footerVideoContent: null,
                topBannerAd,
            },
            {
                headers: await session.getHeaders({
                    'Cache-Control': 'no-cache',
                }),
            },
        );
    }

    const [
        localisation,
        notifications,
        shoppingCartSummary,
        abTestConfig,
        session,
        bottomContent,
        moodBoardPageIds,
        monthlyUsers,
        recentMagazinesResult,
        footerVideoContent,
        topBannerAd,
    ] = await Promise.all([
        getPageLocalisation(args),
        getCombinedNotifications(context),
        shoppingCartAvailable
            ? getShoppingCartSummary(context)
            : Promise.resolve(null),
        context.getABTests(),
        getSession(request),
        getBottomContent(context),
        getMoodBoardPageIds(context),
        getMonthlyUsers(context),
        footerRedesignEnabled
            ? getRecentMagazines({ ...context, limit: 3 })
            : Promise.resolve({ magazines: [] }),
        footerRedesignEnabled
            ? getFooterContent(context)
            : Promise.resolve(null),
        topBannerAdPromise,
    ]);

    // Source the header badge count from the already-loaded cart summary so we don't
    // trigger a second cart read for the notifications payload.
    notifications.cart.count =
        shoppingCartSummary?.ShoppingCart?.ItemCount ?? 0;

    // Currently not being used.
    // const tabletEnabled = hasFeature(user.FeatureFlags, 'enable_tablet');

    const bookConsultationEnabled = false;

    const ecommerceActiveEnabled = hasFeature(featureFlags, 'ecommerce_active');

    const availableForWorkEnabled = hasFeature(
        featureFlags,
        'available_for_work',
    );

    return routerData(
        {
            user,
            featureFlags,
            localisation,
            notifications,
            shoppingCartSummary,
            webSocketUrl,
            ecommerceActiveEnabled,
            bookConsultationEnabled,
            canApproveContent: getPermissions(
                user,
                PermissionEnum.BmContentApprove,
            ),
            canCreateProfessional: getPermissions(
                user,
                PermissionEnum.BmProfessionalCreate,
            ),
            toastMessage: session.getToastMessage(),
            abTestConfig,
            showReseneSponsor: false,
            availableForWorkEnabled,
            professionalBookingLink: null,
            moodBoardPageIds,
            bottomContent,
            filterDrawerCookieValue: getFilterDrawerCookieValue(request),
            monthlyUsers,
            footerRedesignEnabled,
            recentMagazines: recentMagazinesResult.magazines,
            footerVideoContent,
            topBannerAd,
        },
        {
            headers: await session.getHeaders({
                // Make sure user data is not cached.
                'Cache-Control': 'no-cache',
            }),
        },
    );
};

export const shouldRevalidate = shouldRevalidateAppLayout;

const App = () => <AppPage />;

export default App;
